import { useCallback } from 'react';

import { useParams } from '@tanstack/react-router';

import type { AssetsSegmentSchemaWithId } from '@iconik-io/iconik-api/src/services/assets';
import { CommentsPanel } from '@iconik-io/shared-lib/src/react/components/panels/comments_panel/comments_panel';
import { useBasePlayerContext } from '@iconik-io/unified-player-ui-react';

import { assetCommentsReceived } from '../../ducks/asset_comments/slice';
import { selectActingUserId } from '../../ducks/auth/selectors';
import { selectDirectLinkSegmentId } from '../../ducks/route_params/selectors';
import { selectCurrentVersionId } from '../../ducks/version/selectors';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';

import styles from './uad_comments_panel.module.css';

export function UadCommentsPanel() {
  const dispatch = useAppDispatch();
  const { store: unifiedPlayerStore } = useBasePlayerContext();
  const { id: assetId, versionId: routeVersionId } = useParams({
    strict: false,
  });
  const versionId = useAppSelector((state) =>
    selectCurrentVersionId(state, assetId, routeVersionId),
  );
  const userId = useAppSelector(selectActingUserId);
  const share = useAppSelector((state) => state.share.share);
  const directLinkSegmentId = useAppSelector(selectDirectLinkSegmentId);

  const handleSubmitComment = useCallback(
    (segment: AssetsSegmentSchemaWithId) => {
      if (!assetId) {
        return;
      }
      const segmentIds = segment.parent_id ? [] : [segment.id];
      const relations: Record<string, string[]> = segment.parent_id
        ? { [segment.parent_id]: [segment.id] }
        : {};
      dispatch(assetCommentsReceived({ assetId, segmentIds, relations }));
      unifiedPlayerStore.segment.setSelectedSegmentIds([segment.id]);

      const range =
        segment.time_start_milliseconds != null &&
        segment.time_end_milliseconds != null
          ? {
              start: segment.time_start_milliseconds / 1000,
              end: segment.time_end_milliseconds / 1000,
            }
          : undefined;
      unifiedPlayerStore.segment.setCurrentSegment({ id: segment.id, range });
    },
    [assetId],
  );

  // Registers the comment with the player so it can show the comment's drawing.
  const handleCommentCreated = useCallback(
    (segment: AssetsSegmentSchemaWithId) => {
      if (!assetId) {
        return;
      }
      const segmentIds = segment.parent_id ? [] : [segment.id];
      const relations: Record<string, string[]> = segment.parent_id
        ? { [segment.parent_id]: [segment.id] }
        : {};
      dispatch(assetCommentsReceived({ assetId, segmentIds, relations }));
    },
    [assetId],
  );

  if (!assetId) {
    return null;
  }

  return (
    <div className={styles.wrapper}>
      <CommentsPanel
        assetId={assetId}
        versionId={versionId}
        selectedSegmentIds={
          directLinkSegmentId ? [directLinkSegmentId] : undefined
        }
        isDisabled={false}
        isInternalUser
        userId={userId}
        shouldOpenReplyForm
        share={share?.id ? share : undefined}
        onSubmitComment={handleSubmitComment}
        onAutoAnnotationCreated={handleCommentCreated}
        onReplyCreated={handleCommentCreated}
      />
    </div>
  );
}
